import { lazy, useEffect, useState, useRef, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { initSentry } from "@/config/sentry";
import { SentryErrorFallback } from "@/components/ErrorDisplay/SentryErrorFallback";
import {
  AuthProvider,
  getContextLandingPath,
  useAuth,
} from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ThemeProvider as BlueprintThemeProvider } from "styled-components";
import { light as blueprintTheme } from "@resideo/blueprint-theme";
import { ToastContextProvider, GlobalStyle } from "@resideo/blueprint-react";
import { ToastContextProvider as OnePortalToastContextProvider } from "@resideo/blueprint-oneportal";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Login } from "@/pages/Login";
import { ContextSelection } from "@/pages/ContextSelection";
import { Auth0Callback } from "@/pages/Auth0Callback";
import BusinessIntelligence from "@/pages/BusinessIntelligence";
import { DeviceHealth } from "@/pages/DeviceHealth";
import ManageDevices from "@/pages/ManageDevices/ManageDevices";
import { Employees, InviteEmployee, EmployeeDetail } from "@/pages/Employees";
import { Profile } from "@/pages/Profile";
import AddDevice from "@/pages/AddDevice";
import EditControls from "@/pages/EditControls";
// Lazy-loaded so the transitive pilet CSS (Create/Review Weekly Schedule
// module.css) is injected AFTER Blueprint's styled-components runtime sheet.
// An eager import would put those styles first in the cascade and let
// Blueprint's `.dObvlT` win at equal specificity, breaking `.editButton` etc.
// on the pilet's own /thermostat/schedules page too.
const DevicesSchedules = lazy(
  () => import("@/pages/DevicesSchedules/DevicesSchedules"),
);
import { Unauthorized } from "@/pages/Unauthorized";
import BulkEditSettings from "@/pages/BulkEditSettings/BulkEditSettings";
import ReviewApplyPage from "@/pages/BulkEditSettings/ReviewApplyPage";
import ReplaceSchedulePage from "@/pages/BulkEditSettings/ReplaceSchedulePage";
import ManageLocations from "@/pages/ManageLocations/ManageLocations";
import { SignalRProviderWrapper } from "@/contexts/SignalRProviderWrapper";
import { piletLoader } from "@/services/piletLoader";
import { setAuth0TokenGetter } from "@/services/apiService";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import { useTranslation } from "react-i18next";
import { featureFlagService } from "@/services/featureFlagService";
import { permissionService } from "@/services/permissionService";
import "./App.css";
import "./styles/theme.css";

// Context-aware root redirect component
const RootRedirect = () => {
  const { selectedContext, isInitializing, activeRole, user } = useAuth();
  const hasRedirectedRef = useRef(false);

  console.log("🎯 RootRedirect render:", {
    selectedContext,
    isInitializing,
    activeRole,
    hasRedirected: hasRedirectedRef.current,
    userEmail: user?.email,
  });

  // Wait for initialization and active role to be set
  if (isInitializing || !activeRole) {
    console.log("⏳ RootRedirect: Waiting for initialization...", {
      isInitializing,
      activeRole,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        Loading...
      </div>
    );
  }

  // Prevent double redirect
  if (hasRedirectedRef.current) {
    console.log("⏭️ RootRedirect: Already redirected, rendering null");
    return null;
  }

  hasRedirectedRef.current = true;

  const targetPath = getContextLandingPath(
    selectedContext ?? "",
    permissionService.isFeatureEnabled("welcome-onboarding"),
  );
  console.log(
    `🔀 RootRedirect: Redirecting to ${targetPath} (context=${selectedContext}, role=${activeRole})`,
  );
  return <Navigate to={targetPath} replace />;
};

// Initialize Sentry before React renders
initSentry();

function App() {
  const [piletsLoaded, setPiletsLoaded] = useState(false);
  const [piletRoutes, setPiletRoutes] = useState<any[]>([]);
  const [publicRoutes, setPublicRoutes] = useState<any[]>([]);
  const { t } = useTranslation();

  // Check if using mock auth or real Auth0
  const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === "true";

  useEffect(() => {
    // Load pilets on app startup
    const loadResources = async () => {
      try {
        // Initialize feature flag service first (non-blocking)
        await featureFlagService.initialize();
        console.log("✅ Feature flags initialized");

        // Load pilets from npm packages (runtime integration)
        await piletLoader.loadAllPilets();
        setPiletRoutes(piletLoader.getRoutes());
        setPublicRoutes(piletLoader.getPublicRoutes());
        setPiletsLoaded(true);
      } catch (error) {
        console.error("Failed to load resources:", error);
        setPiletsLoaded(true);
      }
    };

    loadResources();
  }, []);

  const auth0Config = {
    domain: import.meta.env.VITE_AUTH0_DOMAIN || "resideo.auth0.com",
    clientId:
      import.meta.env.VITE_AUTH0_CLIENT_ID ||
      "x27JIDVbRAVgDCnItaJjJBIwhk8hWtPC",
    authorizationParams: {
      redirect_uri: `${window.location.origin}/callback`,
      audience:
        import.meta.env.VITE_AUTH0_AUDIENCE || "https://api.oneportal.com",
      scope: "openid profile email offline_access",
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true, // Fallback to non-refresh tokens if offline_access not granted
    cacheLocation: "localstorage" as const,
  };

  // Component to initialize Auth0 token getter and proactive token refresh
  const Auth0TokenInitializer = () => {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

    // Proactively keep the token fresh — refreshes ~60 s before expiry
    useTokenRefresh();

    useEffect(() => {
      // Register an options-aware getter so apiService can request a
      // force-refresh (cacheMode: 'off') when handling 401 responses.
      if (!useMockAuth) {
        setAuth0TokenGetter(async (opts) => {
          try {
            return await getAccessTokenSilently(opts as any);
          } catch (error) {
            console.error("Failed to get Auth0 access token:", error);
            throw error;
          }
        });
      }
    }, [getAccessTokenSilently]);

    // Store Auth0 token in localStorage for Pro-Portal pilet.
    // Also remove it on logout so the Guard View window receives a storage
    // event and can permanently block all new HTTP API calls (while keeping
    // existing WebRTC connections alive).
    useEffect(() => {
      const storeToken = async () => {
        if (isAuthenticated && !useMockAuth) {
          try {
            const token = await getAccessTokenSilently();
            localStorage.setItem("authToken", token);
            console.log("✅ Auth0 token stored in localStorage for Pro-Portal");
          } catch (error) {
            console.error("Failed to store Auth0 token:", error);
          }
        } else if (!isLoading && !isAuthenticated && !useMockAuth) {
          // Only remove after Auth0 has finished initializing (isLoading === false).
          // Removing during initialization would fire the storage event in Guard
          // View windows and permanently lock them on a normal page refresh.
          localStorage.removeItem("authToken");
        }
      };
      storeToken();
    }, [isAuthenticated, isLoading, getAccessTokenSilently]);

    return null;
  };

  const AppContent = () => (
    <BlueprintThemeProvider theme={blueprintTheme}>
      <GlobalStyle />
      <ToastContextProvider>
        <OnePortalToastContextProvider>
        <BrowserRouter>
          <SignalRProviderWrapper>
            <ThemeProvider>
              <AuthProvider>
                {/* Initialize Auth0 token getter if not in mock mode */}
                {!useMockAuth && <Auth0TokenInitializer />}

                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/callback" element={<Auth0Callback />} />
                    <Route
                      path="/context-selection"
                      element={<ContextSelection />}
                    />

                    {/* Public pilet routes (no authentication required) */}
                    {piletsLoaded &&
                      publicRoutes.map((route, index) => (
                        <Route
                          key={`public-${index}`}
                          path={route.path}
                          element={route.element}
                        />
                      ))}

                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={
                          <ProtectedRoute>
                            <RootRedirect />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="onboarding" element={null} />
                      <Route
                        path="business-intelligence"
                        element={<BusinessIntelligence />}
                      />
                      <Route path="device-health" element={<DeviceHealth />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="locations" element={<ManageLocations />} />
                      <Route path="devices" element={<ManageDevices />} />
                      <Route path="devices/add" element={<AddDevice />} />
                      <Route
                        path="devices/edit-controls"
                        element={<EditControls />}
                      />
                      {/* Bulk "Set Schedules" — reuses the pilet's
                          AddPeriodScheduleTypeScreen → CreateWeeklySchedule
                          flow for the thermostats selected on ManageDevices. */}
                      <Route
                        path="devices/schedules"
                        element={
                          <Suspense fallback={null}>
                            <DevicesSchedules />
                          </Suspense>
                        }
                      />

                      {/* Bulk Edit Settings — edit shared settings (including the recording
                          schedule) across multiple cameras. Fully hosted in the shell. */}
                      <Route path="camera/bulk-edit" element={<BulkEditSettings />} />
                      <Route path="camera/bulk-edit/review" element={<ReviewApplyPage />} />
                      <Route path="camera/bulk-edit/replace-schedule" element={<ReplaceSchedulePage />} />

                      {/* Dynamic pilet routes loaded from npm packages */}
                      {piletsLoaded &&
                        piletRoutes.map((route, index) => (
                          <Route
                            key={index}
                            path={route.path}
                            element={route.element}
                          />
                        ))}

                      {/* Placeholder routes for future pilets */}

                      <Route
                        path="thermostat"
                        element={
                          <ProtectedRoute requiredPermission="module.thermostat.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thermostatModule')}</h1>
                              <p>{t('placeholders.thermostatModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="thermostat/schedules"
                        element={
                          <ProtectedRoute requiredPermission="module.thermostat.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thermostatSchedules')}</h1>
                              <p>{t('placeholders.thermostatSchedulesDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="panel"
                        element={
                          <ProtectedRoute requiredPermission="module.panel.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.panelModule')}</h1>
                              <p>{t('placeholders.panelModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="water-leak"
                        element={
                          <ProtectedRoute requiredPermission="module.water-leak.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.waterLeakModule')}</h1>
                              <p>{t('placeholders.waterLeakModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="third-party-devices"
                        element={
                          <ProtectedRoute requiredPermission="module.third-party-devices.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thirdPartyModule')}</h1>
                              <p>
                                This will be provided by the third-party-devices-pilet
                              </p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees"
                        element={
                          <ProtectedRoute featureFlag="employees">
                            <Employees />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees/invite"
                        element={
                          <ProtectedRoute featureFlag="employees">
                            <InviteEmployee />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees/:id"
                        element={
                          <ProtectedRoute featureFlag="employees">
                            <EmployeeDetail />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="users"
                        element={
                          <ProtectedRoute requiredPermission="module.users.view">
                            <div style={{ padding: "24px" }}>
                              <h1>Users Module</h1>
                              <p>This will be provided by the users-pilet</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="roles"
                        element={
                          <ProtectedRoute requiredPermission="module.roles.view">
                            <div style={{ padding: "24px" }}>
                              <h1>Roles Module</h1>
                              <p>This will be provided by the roles-pilet</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    <Route path="/unauthorized" element={<Unauthorized />} />
                  </Routes>
              </AuthProvider>
            </ThemeProvider>
          </SignalRProviderWrapper>
        </BrowserRouter>
        </OnePortalToastContextProvider>
      </ToastContextProvider>
    </BlueprintThemeProvider>
  );

  // Conditionally wrap with Auth0Provider based on environment
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => <SentryErrorFallback {...errorData} />}
      showDialog={false}
    >
      {useMockAuth ? (
        <AppContent />
      ) : (
        <Auth0Provider {...auth0Config}>
          <AppContent />
        </Auth0Provider>
      )}
    </Sentry.ErrorBoundary>
  );
}

export default App;